<template>
  <div id="map">
    <HomeSidebarComponent v-if="filterGroupCollection" ref="sidebar" @clientChange="onClientChange"
      @contractTemplateChange="onContractTemplateChange" @reset="onResetFilter" @filterChange="onFilterChange"
      :filterGroupCollection="filterGroupCollection" />

    <DepotList ref="depotList" :key="depotKey"
      :depot_selected="depot_selected" :models="depots_listed" @onItemClick="onItemClick" @onBookClick="onBookClick" />

    <div class="search">
      <div class="cs-searchbar">
        <i class="cs-search"></i>
        <input class="form-control" v-clearable v-model="state.search" @clear="onClear"
          @keyup.prevent.enter="search(state.search, filterGroupCollection)"
          :placeholder="i18n.app.button.search_by_location" />
      </div>
    </div>

    <MapComponent :models="depots" :myGeometry="myGeometry" :selected_filter_depot_ids="selected_filter_depot_ids"
      :markerColors="markerColors" @onMarkerPress="onMarkerPressed" ref="map" :isLoading="state.loading"
      :showLegend="true" />
  </div>
</template>

<script>
import MapComponent, { MapState } from "@/components/map/MapComponent";
import DepotService from "@/services/DepotService";
import GoogleService from "@/services/GoogleService";
import HomeSidebarComponent from "./partials/HomeSidebarComponent";
import { DepotList } from "@/components/crud/depot";
import FilterGroupCollection from "@/objects/FilterGroupCollection";
import FilterGroup from "@/objects/FilterGroup";
import { mapGetters } from "vuex";
import Client from "@/models/Client";
import omit from "lodash/omit";
import debounce from "lodash/debounce";
import dialogMixin from "@/mixins/dialog.mixin";
import {
  Localforage
} from "@/boot/localforage/initLocalforage";
import { LatLng } from 'leaflet';
import Vue from 'vue';

const depotService = new DepotService();
const googleService = new GoogleService();
const MAX_FOCUSED_DEPOTS = 3;

export default {
  name: "home",
  mixins: [dialogMixin],
  data() {
    return {
      depots: null,
      filterGroups: FilterGroup.list(this.$route.params.filters),
      filterGroupCollection: null,
      markerColors: this.$route.params.marker_colors,
      depots_focused: null,
      depots_listed: null,
      depot_selected: null,
      myGeometry: null,
      selected_filter_depot_ids: [],
      vehicle_status_ticker: null,
      last_search_at: null,
      state: {
        search: "",
        loading: false,
        ready: false,
      }
    };
  },
  mounted() {
    this.state.loading = true;
    this.init(this.contractTemplate, this.client)
      .then(depots => {
        this.depots = depots;
        this.localSearch(this.filterGroupCollection, depots);

        this.startDepotTicker();
        this.search(this.state.search, this.filterGroupCollection).then(() => {
          MapState.clear();
        });
      })
      .finally(() => {
        this.state.loading = false;
      });
  },
  destroyed() {
    this.stopDepotTicker();
  },
  methods: {
    init(ct, client) {
      const filterGroupCollection = new FilterGroupCollection({
        groups: this.filterGroups,
        contract_template: ct,
        client: client
      });

      const filterGroupCollectionSession = FilterGroupCollection.load();

      if (filterGroupCollectionSession)
        filterGroupCollection.copyStateFrom(filterGroupCollectionSession);

      this.filterGroupCollection = filterGroupCollection;

      return new Promise((resolve, reject) => {
        Localforage.getDepots(filterGroupCollection.save_key).then(depots => {
          if (depots?.length && !ct?.is_goplanner_profile)
            return resolve(depots);
          else {
            return this.initialSearch(filterGroupCollection).then(depots => {
              resolve(depots);
            });
          }
        });
      });
    },
    initialSearch(filterGroupCollection, timestamp = null, select = null) {
      const contractTemplateId =
        filterGroupCollection && filterGroupCollection.getContractTemplateId();
      const clientId = filterGroupCollection.client.id;
      const ts = timestamp ? timestamp : this.now();
      const key = filterGroupCollection.save_key;

      return depotService
        .search(null, {
          client_id: clientId,
          contract_template_id: contractTemplateId,
          is_initial_search: true,
          only_changes_from: timestamp,
          select: select
        })
        .then(depots => {
          if (!timestamp) {
            return Localforage.saveDepots(depots, key, ts).then(() => depots);
          } else {
            return depots;
          }
        })
        .catch(e => {
          console.log("unable to search", e);
          console.log(e);
        });
    },
    onClear() {
      this.state.search = null;
      this.depots_listed = [];
      this.depots_focused = [];
      this.search(this.state.search, this.filterGroupCollection);
    },
    now() {
      return parseInt(new Date().getTime() / 1000);
    },
    async search(search, filterGroupCollection, updateMapPosition = true) {
      this.state.ready = false;
      let geometry = null;

      try {
        geometry = search ? await googleService.findByCity(search) : null;
      } catch (e) {
        this.state.ready = true;
        this.showSimpleMessage(i18n.messages.no_search_results);
        return;
      }

      this.myGeometry = geometry;
      filterGroupCollection.save();

      if (this.shouldDoLocalSearch(filterGroupCollection) && !geometry) {
        this.selected_filter_depot_ids =
          filterGroupCollection.findFilterValueByFilterKey("depot_id") || [];
        this.depots_focused = this.depots.filter(
          item => this.selected_filter_depot_ids.indexOf(item.id) > -1
        );

        this.updateDepotStates(this.depots, this.depots);

        this.afterSearch(
          this.depots_focused?.length ? this.depots_focused : this.depots,
          updateMapPosition
        );
      } else {
        this.last_search_at = new Date();

        return new Promise((resolve, reject) => {
          return depotService
            .search(search, {
              client_id: filterGroupCollection.getClientId(),
              coordinate:
                geometry && geometry?.location
                  ? {
                    latitude: geometry.location.lat,
                    longitude: geometry.location.lng
                  }
                  : null,
              contract_template_id: filterGroupCollection.getContractTemplateId(),
              meta: {
                filter: filterGroupCollection.getValue(),
                limit: search ? 3 : 0
              }
            })
            .then(items => {
              this.resetSelection();
              this.selected_filter_depot_ids =
                filterGroupCollection.findFilterValueByFilterKey("depot_id") ||
                [];

              const depotIds = items.map(depot => depot.id);

              this.depots_focused = this.selected_filter_depot_ids.length
                ? items.filter(
                  item => this.selected_filter_depot_ids.indexOf(item.id) > -1
                )
                : search
                  ? items
                    // .filter(item => item.vehicles.length > 0)
                    .slice(0, MAX_FOCUSED_DEPOTS)
                  : [];

              this.updateDepotStates(this.depots, items, !search);

              this.depots_listed = depotIds.map(id => this.depots.find(depot => depot.id == id)).filter(depot => depot);

              this.afterSearch(items, updateMapPosition);

              return resolve(items);
            })
            .catch(e => {
              console.log("unable to search", e);
              console.log(e);
            });
        });
      }

      return Promise.resolve();
    },
    shouldDoLocalSearch(filterGroupCollection) {
      const filters = filterGroupCollection.getFiltersWithValues();
      if (filterGroupCollection.getContractTemplateId()) {
        return false;
      }
      const selectedDepotsIds =
        filterGroupCollection.findFilterValueByFilterKey("depot_id") || [];
      return (
        this.depots &&
        (filters.length == 0 ||
          (filters.length == 1 && selectedDepotsIds.length > 0))
      );
    },
    localSearch(filterGroupCollection, items) {
      this.resetSelection();
      this.selected_filter_depot_ids =
        filterGroupCollection.findFilterValueByFilterKey("depot_id") || [];
      this.updateDepotStates(this.depots, items);

      this.afterSearch(items);
    },
    afterSearch(items, updateMapPosition = true) {

      if (this.$refs.map) {
        this.$refs.map.$forceUpdate();

        setTimeout(() => {
          /* Fuck this... gonna check later why setTimeout is needed */
          this.$refs.map.refreshClusters();
        });

        const mapState = MapState.load();

        if (mapState && mapState.bounds) {
          const sw = new LatLng(mapState.bounds._southWest.lat, mapState.bounds._southWest.lng);
          const ne = new LatLng(mapState.bounds._northEast.lat, mapState.bounds._northEast.lng);

          this.depots_focused = this.depots.filter(
            item => mapState.depot_ids_focused.indexOf(item.id) > -1
          );

          this.depot_selected = this.depots.find(
            item => item.id == mapState.depot_id_selected
          );

          if (updateMapPosition) {
            if (this.myGeometry) {
              this.$refs.map.panTo(this.myGeometry.location);
            } else {
              this.applyLatLngs([
                [sw.lat, sw.lng],
                [ne.lat, ne.lng]
              ]);
            }
          }
        } else if (updateMapPosition) {

          const latlngs = this.myGeometry ? this.depots_focused : items;

          this.applyLatLngs(
            this.getMarkerLatLngs(latlngs)
          );
        }
      }

      // this.$refs.depotList?.$forceUpdate();
      this.state.ready = true;
    },
    startDepotTicker() {
      if (this.depots) this.pingDepots().then(() => this.$forceUpdate());

      this.depot_ticker = setInterval(() => {
        if (this.depots) this.pingDepots().then(() => this.$forceUpdate());
      }, 1 * 15 * 1000);
    },
    stopDepotTicker() {
      if (this.depot_ticker) clearInterval(this.depot_ticker);
    },
    pingDepots() {
      const ts = this.now();
      const fgc = this.filterGroupCollection;
      const key = fgc.save_key;
      let depots = this.depots;

      return new Promise(async (resolve, reject) => {
        try {
          const timestamp = await Localforage.getTs(key);
          const depotsUpdated = await this.initialSearch(fgc, timestamp);
          let hasUpdate = false;

          if (depotsUpdated.length) {
            depotsUpdated.forEach(d => {
              const depot = depots.find(depot => depot.id == d.id);
              if (depot) {
                depot.initializeAttributes(omit(d, ["vehicles"]));
                depot.setVehicles(d.vehicles);
              } else {
                d.initState();
                depots.push(d);
              }
            });

            hasUpdate = true;
          }

          const depotIds = (await this.initialSearch(fgc, 1, ["id"])).map(
            item => item.id
          );

          const depotIdsToDelete = depots
            .map(item => item.id)
            .filter(id => depotIds.indexOf(id) == -1);

          if (depotIdsToDelete.length) {
            hasUpdate = true;
          }

          depots = depots.filter(
            item => depotIdsToDelete.indexOf(item.id) == -1
          );

          Localforage.saveDepots(depots, key, ts).then(() => depots);

          if (hasUpdate) {
            this.$refs.map.$forceUpdate();
            this.onFilterChange(null)
          }

          resolve();
        } catch (e) { }
      });
    },
    refreshView() {
      this.updateDepotStates(
        this.depots,
        this.depots.filter(depot => depot.vehicles.length > 0)
      );
      this.$forceUpdate();
      this.$refs.map.$forceUpdate();

      setTimeout(() => {
        /* Fuck this... gonna check later why setTimeout is needed */
        this.$refs.map.refreshClusters();
      });
    },
    touchDepots() {
      this.depots.forEach(depot => {
        this.touchVehicles(depot.vehicles);
      });

      if (this.depot_selected) this.touchVehicles(this.depot_selected.vehicles);
    },
    touchVehicles(vehicles) {
      vehicles.forEach(vehicle => {
        if (!vehicle.is_available && !vehicle.available_from) {
          const index = vehicles.findIndex(item => item == vehicle);
          vehicles.splice(index, 1);
        }
      });
    },
    onResetFilter() {
      FilterGroupCollection.clear();
      this.filterGroupCollection.reset();
      this.search(this.state.search, this.filterGroupCollection);
    },
    resetSelection() {
      if (this.depot_selected) {
        this.depot_selected.state.focused = false;
        this.depot_selected = null;
      }
      this.selected_filter_depot_ids = [];
    },
    onFilterChange(filter) {
      if (filter?.filter_child) {
        this.filterGroupCollection.applyChildFilters();
        this.$forceUpdate();
      }

      debounce(() => {
        this.search(this.state.search, this.filterGroupCollection, !!filter)
      }, 500)();
    },
    onClientChange(client) {
      this.reset(null, client);
    },
    onContractTemplateChange(ct) {
      if (!this.state.ready) {
        return;
      }

      const client = new Client(
        ct
          ? this.clients.find(item => item.id == ct.client_id)
          : this.filterGroupCollection.client
      );

      this.reset(ct, client);
    },
    reset(ct, client) {
      this.state.ready = false;

      // this.$nextTick(function () {
        this.resetSelection();
        FilterGroupCollection.clear();
        this.stopDepotTicker();

        this.depots = [];
        this.depots_focused = null;

        this.init(ct, client).then(depots => {
          this.depots = depots || [];
          this.localSearch(this.filterGroupCollection, depots);

          this.startDepotTicker();
          this.search(this.state.search, this.filterGroupCollection);
        });
      // });
    },
    getVehicles() {
      let vehicles = [];
      this.depots.forEach(depot => {
        vehicles = vehicles.concat(depot.vehicles);
      });

      return vehicles;
    },
    getMarkerLatLngs(items) {
      let fitBoundsItems = items;

      if (
        this.selected_filter_depot_ids &&
        this.selected_filter_depot_ids.length
      ) {
        fitBoundsItems = items.filter(
          item => this.selected_filter_depot_ids.indexOf(item.id) > -1
        );
      }

      return fitBoundsItems.map(item => [
        item.address.latitude,
        item.address.longitude
      ]);
    },
    applyLatLngs(latlngs) {
      if (latlngs.length > 1) {
        this.$refs.map.fitBounds(latlngs);
      } else if (latlngs.length == 1) {
        const latlng = latlngs[0];

        this.$refs.map.panTo({
          lat: latlng[0],
          lng: latlng[1]
        });
      }
    },
    onMarkerPressed(depot) {
      if (
        this.depot_selected &&
        this.selected_filter_depot_ids.indexOf(this.depot_selected.id) == -1
      ) {
        this.depot_selected.state.selected = false;
      }

      this.depot_selected = depot;
      this.depot_selected.state.selected = true;
    },
    onItemClick(depot) {
      this.$refs.map.onMarkerPress(depot);
    },
    onBookClick({ vehicle, depot }) {
      MapState.save(
        this.$refs.map.getBounds(),
        this.depot_selected?.id,
        this.depots_focused ? this.depots_focused.map(item => item.id) : []
      );

      this.$router.push({
        name: "contract-create",
        query: {
          client_id: this.filterGroupCollection.getClientId(),
          vehicle_id: vehicle.id,
          depot_id: depot.id,
          contract_template_id: this.filterGroupCollection.getContractTemplateId()
        }
      });
    },
    getStateForDepot(depot) {
      if (!depot.state) depot.initState();
      const selectedIds = this.selected_filter_depot_ids;

      selectedIds.indexOf(depot.id) > -1 ? depot.select() : depot.deselect();

      if (depot.address && item.address)
        depot.state.distance = item.address.distance;

      depot.state.available_vehicles = item.vehicles;

      if (!selectedIds.length)
        depot.state.focused = depot.state.available_vehicles.length > 0;

      return depot.state;
    },
    updateDepotStates(depotsAll, depotsWithVehicles = [], fallbackToZero = true) {
      // ABC-45 / ARVAL-BRAVO-CARS-FRONTEND-39: localSearch can run while depots
      // are still null (e.g. login shell / failed init), which threw on forEach.
      if (!Array.isArray(depotsAll)) return;
      if (!depotsWithVehicles) depotsWithVehicles = [];
      const ids = depotsWithVehicles.map(item => item.id);

      depotsAll.forEach(depot => {
        const selected = this.selected_filter_depot_ids.indexOf(depot.id) > -1;

        depot.initState({
          available_vehicles:
            ids.indexOf(depot.id) == -1 && fallbackToZero ? [] : depot.state?.available_vehicles,
          selected: selected,
          focused: selected
        });
      });

      depotsWithVehicles.forEach(item => {
        const depot = depotsAll.find(depot => depot.id == item.id);

        if (depot) {
          if (depot.address && item.address)
            depot.state.distance = item.address.distance;

          depot.setAvailableVehicles(item.vehicles);

          if (!this.selected_filter_depot_ids.length) {
            depot.state.focused = depot.state.available_vehicles.length > 0 || this.myGeometry;
          }
        }
      });
    },
  },
  computed: {
    ...mapGetters({
      contractTemplate: "contractTemplate",
      client: "client",
      clients: "clients"
    }),
    depotKey() {
      let key = 'depot-list-';

      if (this.last_search_at) {
        key += this.last_search_at.getTime() + '-';
      }

      if (this.filterGroupCollection?.getContractTemplateId()) {
        key += this.filterGroupCollection.getContractTemplateId() + '-';
      }

      key += this.state.ready

      return key;
    },
  },
  components: {
    HomeSidebarComponent,
    DepotList,
    MapComponent
  }
};
</script>
